import { useState, useEffect } from "react";
import { Button, Image, Form } from "antd";
import { BaseForm } from "components/common/forms/BaseForm";
import MicrosoftIcon from "components/common/MicrosoftIcon";
import {
  getErrorMessageString,
  getMenuItemsBasedOnPermissions,
  getRoleBasedLandingPage,
} from "utils/utils";
import { notificationController } from "components/common/notificationController";
import { useNavigate } from "react-router-dom";
import { messageStrings, stringConfig, userString } from "config/site.string";
import apiService from "services/apiservice";
import { TimeOutIcon } from "config/IconConfig";
import { saveState } from "library/helpers/sessionStorage";
import { setLoggedInUserInfo } from "redux/users/actions";
import { useDispatch } from "react-redux";
import CustomLoader from "components/common/Loader/CustomLoader";
import { msalConfig } from "authConfig";
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { InteractionType } from "@azure/msal-browser";
import { PublicClientApplication } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  BackgroundWrapper,
  FormItem,
  FormWrapper,
  LoginWrapper,
  Wrapper,
} from "./styles";
import * as Auth from "./styles";
import LogoContainer from "./LogoContainer";

const msalInstance = new PublicClientApplication(msalConfig);

const LoginPage = ({ setUserInfo = null, sessionExpired = false }: any) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [msalLoading, setMsalLoading] = useState(false);

  useEffect(() => {
    initializeMsal().catch(console.error);
  }, []);

  const initializeMsal = async () => {
    await msalInstance.initialize();
    await msalInstance
      .handleRedirectPromise()
      .then((response) => {
        if (response !== null) {
          msalInstance.setActiveAccount(response.account);
          saveState("msal_token", response.accessToken);
        }
      })
      .catch((error) => {
        console.error("Error handling redirect:", error);
      });
  };

  const handleLogin = async () => {
    try {
      setMsalLoading(true);
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup({
        scopes: ["user.read"],
      });

      if (loginResponse.account) {
        const account = loginResponse.account;

        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["user.read"],
            account: account,
          });

          const payload = {
            email: account.username,
            msalAccessToken: tokenResponse.accessToken,
          };

          const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
            msalInstance,
            {
              account: account,
              scopes: ["User.Read"],
              interactionType: InteractionType.Popup,
            },
          );

          const graphClient = Client.initWithMiddleware({ authProvider });

          const [userResponse, loginDetailsResponse] = await Promise.all([
            graphClient.api("/me").select("givenName,surname").get(),
            apiService.post("/GetMsalUserLoginDetails/", payload, {
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }),
          ]);

          if (
            loginDetailsResponse.data === null ||
            loginDetailsResponse.data?.userDetails?.isActive === false
          ) {
            notificationController.error({
              message: "Access Denied",
              description:
                "You don't have login access. Please contact the administrator.",
            });
            return;
          }

          const updatePayload = {
            email: account.username,
            firstName: userResponse.givenName,
            lastName: userResponse.surname,
          };

          await apiService.post("/UpdateMsalUserDetails/", updatePayload, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${loginDetailsResponse.data?.accessToken}`,
            },
          });

          saveState("userInfo", {
            ...loginDetailsResponse.data?.userDetails,
            isMicrosoftUser: true,
          });
          saveState("loggedInUser", loginDetailsResponse.data);
          dispatch(setLoggedInUserInfo(loginDetailsResponse.data?.userDetails));
          await getUserMenuList(
            loginDetailsResponse.data?.userDetails?.permissions,
          );
        } catch (error) {
          handleLoginError(error);
        }
      }
    } catch (error) {
      handleLoginError(error);
    } finally {
      setMsalLoading(false);
    }
  };

  const handleLoginError = (e: any) => {
    const errorMessages = getErrorMessageString(e);
    if (errorMessages !== undefined && e?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Login failed. ${errorMessages}`,
      });
    } else if (e?.response !== undefined) {
      notificationController.error({
        message: "Error",
        description: "Login failed.",
      });
    }
  };

  const onFinish = async (_values: any) => {
    getUserLoginDetails();
  };

  const getUserMenuList = async (permissions: string) => {
    const actualPermissions = getMenuItemsBasedOnPermissions(permissions);
    saveState("actualPermissionsList", actualPermissions);
    getRoleBasedLandingPage(actualPermissions, navigate);
  };

  const getUserLoginDetails = async () => {
    try {
      setLoading(true);
      const payload: any = {
        email: email,
        password: password,
      };
      const { data } = await apiService.post(`/GetUserLoginDetails/`, payload);
      if (data === null || data === "") {
        notificationController.error({
          message: "Access Denied",
          description: "Invalid login credentials.",
        });
      } else if (data?.userDetails?.isActive === false) {
        notificationController.error({
          message: "Access Denied",
          description:
            "You don't have login access. Please contact the administrator.",
        });
      } else {
        dispatch(setLoggedInUserInfo(data?.userDetails));
        saveState("userInfo", { ...data?.userDetails, isMicrosoftUser: false });
        saveState("loggedInUser", data);
        if (setUserInfo !== null) {
          setUserInfo(data?.usersDetails);
        }
        await getUserMenuList(data?.userDetails?.permissions);
        setLoading(false);
      }
    } catch (e: any) {
      setLoading(false);
      const errorMessages = getErrorMessageString(e);
      if (errorMessages !== undefined && e?.response?.status !== 401)
        notificationController.error({
          message: "Error",
          description: `Login failed. ${errorMessages}`,
        });
      else if (e?.response?.status !== 401) {
        notificationController.error({
          message: "Error",
          description: `Login failed.`,
        });
      }
    }
  };

  return (
    <Wrapper>
      <BackgroundWrapper>
        <LoginWrapper>
          <FormWrapper>
            <LogoContainer />
            {sessionExpired === true && (
              <>
                <div className="logo-container">
                  <Image
                    src={TimeOutIcon}
                    preview={false}
                    alt="logo"
                    height={50}
                  />
                </div>
                <div style={{ textAlign: "center" }}>
                  <Auth.FormTitle>YOUR SESSION HAS BEEN EXPIRED</Auth.FormTitle>
                </div>
                <div style={{ textAlign: "center" }}>
                  <Auth.InitialInfo>
                    Please login again to continue
                  </Auth.InitialInfo>
                </div>
              </>
            )}
            <BaseForm layout="vertical" requiredMark="optional">
              {(msalLoading || loading) && <CustomLoader />}
              <FormItem>
                <Button
                  onClick={handleLogin}
                  style={{
                    width: "100%",
                    height: "55px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  icon={<MicrosoftIcon style={{ fontSize: "18px" }} />}
                >
                  <span style={{ marginLeft: "8px", fontSize: "20px" }}>
                    Pegasus Auth
                  </span>
                </Button>
              </FormItem>
              <div
                style={{
                  width: "100%",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    flexGrow: 1,
                    height: "1px",
                    backgroundColor: "#E5E7EB",
                  }}
                />
                <span
                  style={{
                    padding: "0 16px",
                    color: "#6B7280",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  OR
                </span>
                <div
                  style={{
                    flexGrow: 1,
                    height: "1px",
                    backgroundColor: "#E5E7EB",
                  }}
                />
              </div>
            </BaseForm>
            <Form
              layout="vertical"
              form={form}
              name="login"
              onFinish={onFinish}
            >
              <FormItem
                label=""
                name="email"
                rules={[
                  {
                    required: true,
                    message: userString.emailAddressRequired,
                  },
                  {
                    type: "email",
                    message: messageStrings.emailNotValid,
                  },
                ]}
              >
                <Auth.FormInput
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </FormItem>
              <Auth.FormItem
                label=""
                name="password"
                rules={[
                  {
                    required: true,
                    message: stringConfig.passwordRequired,
                  },
                  {
                    pattern: /^[^ ]+$/,
                    message: "Password is invalid.",
                  },
                ]}
              >
                <Auth.FormInputPassword
                  placeholder="password"
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    width: "100%",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Auth.FormItem>
              <Auth.FormItem>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    width: "100%",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  loading={loading}
                  disabled={loading}
                >
                  Login
                </Button>
              </Auth.FormItem>
            </Form>
          </FormWrapper>
        </LoginWrapper>
      </BackgroundWrapper>
    </Wrapper>
  );
};

export default LoginPage;
